import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import QrCode from "../common/QrCode";
import { getHostToken, hostLink } from "../../session/hostTokens";
import { getHostKey, hostKeyLink } from "../../session/hostKey";
import { copyText, invitationLink, shortLink } from "../../lib/clipboard";

/** The invitation: QR code, join code, link, share and copy. Also on the set editor. */
export const InviteCard = ({ compact = false }) => {
  const code = useSelector((state) => state.session.Code);
  const link = invitationLink(code);
  return (
    <div className="qr-card" data-screen-label="Invite">
      <QrCode value={link} size={compact ? 160 : 240} className={compact ? "qr-img qr-img-sm" : "qr-img"} />
      <div className="grow">
        <div className="kicker kicker-lg" style={{ color: "#8a7a4e" }}>
          {compact ? "Join code" : "Code"}
        </div>
        <div className={`qr-code${compact ? " qr-code-sm" : ""}`}>{code}</div>
        {!compact && <div className="qr-link">{shortLink(code)}</div>}
      </div>
    </div>
  );
};

const WelcomePage = () => {
  const navigate = useNavigate();
  const sessionCode = useSelector((state) => state.session.Code);
  const hostKey = getHostKey();
  const hostToken = getHostToken(sessionCode);
  const [copied, setCopied] = useState(null); // "link" | "key" | "host" | "share"
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const link = invitationLink(sessionCode);

  // the host key runs every session of this host; older devices may only hold the session's token
  const runLink = hostKey ? hostKeyLink(hostKey, sessionCode) : hostToken ? hostLink(sessionCode, hostToken) : null;

  const flash = (what) => {
    setCopied(what);
    setTimeout(() => setCopied(null), 2000);
  };

  const copy = async (text, what) => {
    if (await copyText(text)) flash(what);
  };

  const share = async () => {
    try {
      await navigator.share({ title: "Sing At", text: `Join live lyrics. Code: ${sessionCode}`, url: link });
      flash("share");
    } catch {
      // the user closed the share sheet
    }
  };

  return (
    <div className="page" data-screen-label="Welcome">
      <h2 className="page-title">Welcome to your new session</h2>
      <InviteCard />
      <div className="btn-row">
        {canShare && (
          <button type="button" className="btn btn-outline" onClick={share}>
            {copied === "share" ? "Shared" : "Share"}
          </button>
        )}
        <button type="button" className="btn btn-outline" onClick={() => copy(`Join live lyrics at ${link}\nCode: ${sessionCode}`, "link")}>
          {copied === "link" ? "Copied!" : "Copy link"}
        </button>
      </div>
      {(hostKey || runLink) && (
        <div className="card" data-screen-label="Host key">
          <div className="kicker kicker-lg" style={{ marginBottom: 8 }}>
            Your host key
          </div>
          {hostKey && <div className="mono">{hostKey}</div>}
          <div className="wrap" style={{ marginTop: 12 }}>
            {hostKey && (
              <button type="button" className="btn btn-sm btn-outline" onClick={() => copy(hostKey, "key")}>
                {copied === "key" ? "Copied!" : "Copy host key"}
              </button>
            )}
            {runLink && (
              <button type="button" className="btn btn-sm btn-outline" onClick={() => copy(runLink, "host")}>
                {copied === "host" ? "Copied!" : "Copy host link"}
              </button>
            )}
          </div>
          <p className="note" style={{ marginTop: 10 }}>
            {hostKey
              ? "Keep the key private. Type it into the code box on another device, or open the host link there, to run your sessions and use your songs from it."
              : "Open it on another device to run the session from there too."}
          </p>
        </div>
      )}
      <div style={{ flex: 1 }} />
      <button type="button" className="btn btn-lg btn-dark btn-block" onClick={() => navigate("/sets")}>
        Start
      </button>
    </div>
  );
};

export default WelcomePage;
